import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Form8991Service } from './form8991.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-form8991',
    templateUrl: './form8991.component.html',
    styleUrls: ['./form8991.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Form8991Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  modalTitle = 'Form 8991 - Tax on Base Erosion Payments';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  selectedTabName = 'Part I';
  currentPart = 'P1';
  currentSchedule = 'NA';

  activeTab = 0;
  tabs = [
    { id: 0, name: 'Part I', part: 'P1', schedule: 'NA' },
    { id: 1, name: 'Part II', part: 'P2', schedule: 'NA' },
    { id: 2, name: 'Part III', part: 'P3', schedule: 'NA' },
    { id: 3, name: 'Part IV', part: 'P4', schedule: 'NA' },
    { id: 4, name: 'Schedule A', part: 'NA', schedule: 'A' },
    { id: 5, name: 'Schedule B', part: 'NA', schedule: 'B' },
    { id: 6, name: 'Schedule C', part: 'NA', schedule: 'C' }
  ];

  formData: any[] = [];
  f8991FormData: any[] = [];
  f8991OriginalData: any[] = [];
  f8991Header: any = {};
  entityList: Record<string, string> = {};
  unAllocatedAmt = 0;
  convensions: string[] = ['Half-year', 'Mid-quarter'];
  methods: string[] = ['200 DB', '150 DB', 'SL'];
  recoveryPeriod: string[] = ['3', '5', '7', '10', '15', '20'];
  CountriesList: any = { values: [] };
  allCountries: any[] = [];
  logged_in_user = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private form8991Service: Form8991Service
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.setupEntityData();
    this.logged_in_user = this.USER_SETTINGS?.sso_id ?? this.globalService?.sso_id ?? '';
    this.modalTitle = this.f8991Header?.ATTRIB_DESCRIPTION || 'Form 8991 - Tax on Base Erosion Payments of Taxpayers with Substantial Gross Receipts';
    if (this.currentSchedule === 'B') {
      this.fetchCountryList();
    }
    this.loadData();
  }

  private setupEntityData(): void {
    this.entityList = {
      'Tax Year': this.rowData?.TAX_YEAR ?? '',
      'LEID': this.rowData?.LEID ?? '',
      'LE Name': this.rowData?.LE_NAME ?? ''
    };
  }

  private processFormData(list: any[]): void {
    if (!list || list.length === 0) {
      this.f8991FormData = [];
      return;
    }
    const o: Record<number, any[]> = {};
    list.forEach((b: any) => {
      const k = b.LINE_ORDER;
      if (!o[k]) o[k] = [];
      o[k].push(b);
    });
    let a = Object.keys(o).map((k) => {
      const grp = o[Number(k)];
      return { ROW_TYPE: grp[0].ROW_TYPE, DELETE_ROWS: [], Line_data: grp };
    });
    const LH_index = a.findIndex((x) => x.ROW_TYPE === 'LH');
    const n: Record<string, any[]> = {};
    a.forEach((row) => {
      const k = row.ROW_TYPE;
      if (!n[k]) n[k] = [];
      n[k].push(row);
    });
    let lhBlock: any[] = [];
    if (n['LH'] && n['LH'].length > 0) {
      lhBlock = [{ ROW_TYPE: n['LH'][0].ROW_TYPE, DELETE_ROWS: [], Line_data: n['LH'] }];
    }
    for (let i = a.length - 1; i >= 0; i--) {
      if (a[i].ROW_TYPE === 'LH') a.splice(i, 1);
    }
    if (lhBlock.length > 0 && LH_index >= 0) {
      a.splice(LH_index, 0, lhBlock[0]);
    }
    a.forEach((row, i) => {
      if (row.ROW_TYPE === 'DT') {
        row.Line_data.forEach((line: any, j: number) => {
          if (line.EDIT_TYPE === 'label' && line.DATA_TYPE === 'string' && line.IS_EDITABLE === 'Y' && line.ATTRIB_VALUE === undefined) {
            line.ATTRIB_VALUE = line.ATTRIB_DESCRIPTION;
          }
          if (line.EDIT_TYPE === 'currency' && line.DATA_TYPE === 'number' && line.IS_EDITABLE === 'Y' && line.ATTRIB_VALUE != null) {
            line.ATTRIB_VALUE = parseInt(line.ATTRIB_VALUE, 10);
          }
        });
      } else if (row.ROW_TYPE === 'LH') {
        row.Line_data.forEach((parent: any) => {
          (parent.Line_data || []).forEach((line: any) => {
            if (line.IS_EDITABLE === 'Y' && (line.EDIT_TYPE === 'label' || line.EDIT_TYPE === 'text') && line.DATA_TYPE === 'string' && line.ATTRIB_VALUE === undefined) {
              line.ATTRIB_VALUE = line.ATTRIB_DESCRIPTION;
            }
            if (line.IS_EDITABLE === 'Y' && line.EDIT_TYPE === 'currency' && line.DATA_TYPE === 'number' && line.ATTRIB_VALUE != null) {
              line.ATTRIB_VALUE = parseInt(line.ATTRIB_VALUE, 10);
            }
          });
        });
      }
    });
    this.f8991FormData = a;
    this.f8991OriginalData = safeJsonInput<any>(JSON.stringify(a), null, 'form8991.parse1');
    this.cdr.detectChanges();
  }

  valuechanged(mainIndex: number, occurrence: any, lineNo: any, index: number): void {
    this.f8991FormData.forEach((row, i) => {
      if (row.ROW_TYPE === 'CH') {
        const parent = row.Line_data?.[mainIndex];
        if (parent && parent.Line_no === lineNo && parent.Occurrence === occurrence && parent.Line_data_occ?.[index + 1]) {
          parent.Line_data_occ[index + 1].IS_CHANGED_FLAG = 'Y';
          parent.Line_data_occ[index + 1].HAS_ERROR = 'N';
        }
      } else if (row.ROW_TYPE === 'DT' || row.ROW_TYPE === 'RT') {
        if (i === mainIndex && row.Line_data?.[index]) {
          row.Line_data[index].IS_CHANGED_FLAG = 'Y';
        }
      } else if (row.ROW_TYPE === 'LH') {
        const parent = row.Line_data?.[mainIndex];
        if (parent?.Line_data?.[index]?.LINE_NO === lineNo) {
          parent.Line_data[index].IS_CHANGED_FLAG = 'Y';
        }
      }
    });
    this.cdr.detectChanges();
  }

  updateAmount(index: number, lineNo: any): void {
    this.calcFinalAmounts();
  }

  calcFinalAmounts(): void {
    this.f8991FormData.forEach((row) => {
      if (row.ROW_TYPE === 'LH') {
        (row.Line_data || []).forEach((parent: any) => {
          (parent.Line_data || []).forEach((line: any) => {
            if (line.ATTRIB_CALC_FORMULA != null) {
              line.ATTRIB_VALUE = this.calc_final_amt(line.ATTRIB_CALC_FORMULA);
              line.IS_CHANGED_FLAG = 'Y';
            }
          });
        });
      } else if (row.ROW_TYPE === 'DT') {
        (row.Line_data || []).forEach((line: any) => {
          if (line.ATTRIB_CALC_FORMULA != null) {
            line.ATTRIB_VALUE = this.calc_final_amt(line.ATTRIB_CALC_FORMULA);
            line.IS_CHANGED_FLAG = 'Y';
          }
        });
      }
    });
    this.cdr.detectChanges();
  }

  calc_final_amt(formula: string): number {
    if (!formula) return 0;
    const lineFormulaArray = formula.split(/[+\-/*]/);
    lineFormulaArray.forEach((part) => {
      if (part.indexOf('(') > -1 && part.indexOf(')') > -1) {
        const code = part.replace(/"/g, '').replace(/'/g, '').replace(/\(|\)/g, '').trim();
        let lineAmt = 0;
        this.f8991FormData.forEach((row) => {
          if (row.ROW_TYPE === 'LH') {
            (row.Line_data || []).forEach((parent: any) => {
              const found = (parent.Line_data || []).find((l: any) => l.ATTRIB_SHORT_CODE === code);
              if (found && found.ATTRIB_VALUE != null) lineAmt += parseInt(String(found.ATTRIB_VALUE), 10);
            });
          } else {
            const found = (row.Line_data || []).find((l: any) => l.ATTRIB_SHORT_CODE === code);
            if (found && found.ATTRIB_VALUE != null) lineAmt += parseInt(String(found.ATTRIB_VALUE), 10);
          }
        });
        formula = formula.replace('(' + code + ')', String(lineAmt));
      }
    });
    try {
      const result = safeEvalFormula(formula, null, 'form8991');
      return typeof result === 'number' && !isNaN(result) ? result : 0;
    } catch {
      return 0;
    }
  }

  private fetchCountryList(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.form8991Service.loadCountries(baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        const arr = data?.jsonObject || data || [];
        this.allCountries = arr.map((c: any) => ({ LABEL: c.COUNTRY_DESC, VALUE: c.COUNTRY_CODE }));
        this.CountriesList = { param_name: 'Countries', values: this.allCountries, label_name: 'label', value_name: 'value', label: 'Countries', selected: [] };
        this.cdr.detectChanges();
      }
    });
  }

  private loadData(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.showFormLoader = true;
    this.cdr.detectChanges();

    this.form8991Service.loadFormData(
      baseUrl,
      this.rowData,
      this.currentPart,
      this.currentSchedule,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        const list = Array.isArray(data) ? data : (data?.jsonObject || []);
        if (list.length > 0) {
          let raw = list;
          if (list[0]?.ROW_TYPE === 'FH') {
            this.f8991Header = list[0];
            raw = list.slice(1);
          }
          this.processFormData(raw);
        } else {
          this.gtwSnackbarService.openSnackBar('No data found', 'gtw-snackbar--red');
          this.f8991FormData = [];
          this.f8991OriginalData = [];
        }
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading Form 8991 data:', err);
        this.gtwSnackbarService.openSnackBar('Error loading form data', 'gtw-snackbar--red');
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  onTabChange(tab: { id: number; name: string; part: string; schedule: string }): void {
    this.activeTab = tab.id;
    this.selectedTabName = tab.name;
    this.currentPart = tab.part;
    this.currentSchedule = tab.schedule;
    if (this.currentSchedule === 'B') {
      this.fetchCountryList();
    }
    this.loadData();
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) return;
    const parsed = safeJsonInput<any>(JSON.stringify(this.f8991FormData), null, 'form8991.parse2');
    if (JSON.stringify(parsed) === JSON.stringify(this.f8991OriginalData)) {
      this.cancel();
      return;
    }
    if (this.unAllocatedAmt !== 0 && !confirm('Total Depreciation Expense Per Tax Return is not fully adjusted. Confirm to save the changes.')) {
      return;
    }
    const finalData: any[] = [];
    this.f8991FormData.forEach((row) => {
      if (row.ROW_TYPE === 'TH' || row.ROW_TYPE === 'DT' || row.ROW_TYPE === 'RT') {
        (row.Line_data || []).forEach((line: any) => finalData.push(line));
      } else if (row.ROW_TYPE === 'LH') {
        (row.Line_data || []).forEach((parent: any) => {
          (parent.Line_data || []).forEach((line: any) => {
            if (line.EDIT_TYPE === 'custom_date_various' && line.DATA_TYPE === 'dateVarious' && line.ATTRIB_VALUE) {
              line.ATTRIB_VALUE = this.getDateFormat(line.ATTRIB_VALUE);
            }
            finalData.push(line);
          });
        });
      }
    });
    const changedOnly = finalData.filter((el) => el.IS_CHANGED_FLAG === 'Y');
    const clob_settings = [{ sso_id: this.logged_in_user, screen_key: this.globalService?.screen_key }];
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    this.form8991Service.saveFormData(baseUrl, changedOnly, clob_settings, this.currentPart, this.currentSchedule, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const body = res?.body ?? res;
        const errMsg = body?.errorMessage ?? body?.data?.errorMessage ?? res?.errorMessage;
        if (errMsg && String(errMsg) !== 'null') {
          this.gtwSnackbarService.openSnackBar(errMsg, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Form 8991 Details are successfully updated.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.gtwSnackbarService.openSnackBar(err?.message || 'Save failed', 'gtw-snackbar--red');
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getDateFormat(dateVal: any): string {
    if (typeof dateVal === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateVal)) return dateVal;
    if (dateVal === 'VARIOUS') return 'VARIOUS';
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return m + '/' + day + '/' + d.getFullYear();
  }

  reset(): void {
    this.f8991FormData = safeJsonInput<any>(JSON.stringify(this.f8991OriginalData), null, 'form8991.parse3');
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
